import {getTestProps} from '@benchling/js-test-utils';
import {Button, Group} from '@mantine/core';
import React, {useState} from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Section} from 'constants/NavConstants';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import {ButtonStyle} from 'ui-library/buttons/Button';
import strUtil from 'util/strUtil';
import useRecentWorklistsQuery from 'worklists/useRecentWorklistsQuery';
import WorklistCreateButton from 'worklists/WorklistCreateButton';
import {openWorklistListing} from 'worklists/WorklistUIActions';

import CardSection, {
  CardSectionMessageRow,
  CardSectionRow,
  CardSectionRowList,
  CardSectionRowProps,
  renderQueryLoadingOrError,
} from './CardSection';
import {
  HOMEPAGE_SECTION_TEST_COMPONENT,
  INITIAL_ROWS_TO_DISPLAY,
  MAX_ROWS_TO_DISPLAY,
} from './HomepageConstants';

const TEST_COMPONENT = HOMEPAGE_SECTION_TEST_COMPONENT.WORKLISTS;

export default function WorklistsSection(): JSX.Element {
  const {queryResultType, recentWorklists} = useRecentWorklistsQuery();
  const [showMore, setShowMore] = useState(false);

  const worklistsSectionRows: Array<CardSectionRowProps> | undefined = recentWorklists
    ?.slice(0, showMore ? MAX_ROWS_TO_DISPLAY : INITIAL_ROWS_TO_DISPLAY)
    .map((worklist) => ({
      id: worklist.id,
      dataTestComponent: TEST_COMPONENT,
      icon: ModelTypeConstants.WORKLIST,
      name: worklist.name,
      modifiedAt: worklist.modifiedAt,
      count: worklist.items.length,
      onClick: () => {
        openWorklistListing(worklist);
      },
      rightContent: strUtil.toSentenceCase(worklist.itemType),
    }));
  const loadingOrError = renderQueryLoadingOrError(
    {type: queryResultType},
    {loading: 'Loading worklists...', error: 'Error loading worklists'},
    TEST_COMPONENT
  );
  return (
    <CardSection
      title="Worklists"
      dataTestAttrs={{component: TEST_COMPONENT, element: 'root'}}
      rightContent={
        <Group>
          <WorklistCreateButton buttonStyle={ButtonStyle.SECONDARY} />{' '}
          <Button
            size="sm"
            onClick={() => {
              SidepanelActions.openSection(Section.WORKLISTS);
            }}
            {...getTestProps({component: TEST_COMPONENT, element: 'seeAllButton'})}
          >
            See all
          </Button>
        </Group>
      }
    >
      {loadingOrError}
      {queryResultType === QueryResultType.SUCCESS &&
        (!worklistsSectionRows!.length ? (
          <CardSectionMessageRow
            message="No recent worklists"
            dataTestAttrs={{component: TEST_COMPONENT, element: 'messageRow', key: 'empty'}}
          />
        ) : (
          <CardSectionRowList>
            {worklistsSectionRows!.map((row) => (
              <CardSectionRow key={row.id} {...row} />
            ))}
          </CardSectionRowList>
        ))}
      {recentWorklists != null && recentWorklists.length > INITIAL_ROWS_TO_DISPLAY && (
        <CardSectionMessageRow
          message={showMore ? 'Show less' : 'Show more'}
          onClick={() => {
            setShowMore(!showMore);
          }}
        />
      )}
    </CardSection>
  );
}
